import {isMediaString, type MediaString} from '@wandb/weave/common/types/media';
import {Icon} from '@wandb/weave/components/Icon';
import * as RadixTooltip from '@wandb/weave/components/RadixTooltip';
import {RemovableTag, RemoveAction} from '@wandb/weave/components/Tag';
import {clsx} from 'clsx';
import * as React from 'react';

import {
  type Maybe,
  RunValueSuggestionsPaginatedDocument,
} from '../../generated/graphql';
import * as Run from '../../util/runs';
import {makeInfiniteDropdown} from './InfiniteDropdown';

const Container = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
  }
>(({children}, ref) => {
  return (
    <div ref={ref} className="relative max-w-[400px] flex-grow">
      {children}
    </div>
  );
});

const Display = ({
  isOpen,
  selectedItems,
  removeFilter,
  isMultiSelect = false,
  isValid = true,
  toggleMenu,
  ...props
}: {
  isOpen: boolean;
  selectedItems: string[];
  removeFilter: (item: string) => void;
  isMultiSelect: boolean;
  isValid?: boolean;
  toggleMenu: () => void;
}) => {
  const classes = clsx(
    'max-h-[250px] overflow-y-auto',
    'min-h-[44px] min-w-[220px]',
    'w-full py-[8px] px-[13px] sticky flex flex-wrap items-center gap-4 justify-items-start',
    isValid ? 'bg-[rgba(46,120,199,0.08)]' : 'bg-red-300/[.32]'
  );

  if (selectedItems.length === 0) {
    return (
      <div
        className={classes}
        onClick={toggleMenu}
        tabIndex={0}
        data-test="filter-value"
        {...props}>
        <div className="self-center">Select a value...</div>
      </div>
    );
  }

  return (
    <div
      className={classes}
      onClick={toggleMenu}
      tabIndex={0}
      data-test="filter-value"
      {...props}>
      {isMultiSelect ? (
        selectedItems.map(selItem => {
          return (
            <RemovableTag
              color="blue"
              key={`selected-item-${selItem}`}
              label={selItem ?? ''}
              removeAction={
                <RemoveAction
                  onClick={(e: any) => {
                    e.stopPropagation();
                    removeFilter(selItem);
                  }}
                />
              }
              Wrapper={null}></RemovableTag>
          );
        })
      ) : (
        <div className="block w-[400px] items-center truncate p-2">
          {selectedItems[0]}
        </div>
      )}
    </div>
  );
};
const ListWrapper = React.forwardRef<
  HTMLUListElement,
  {
    children: React.ReactNode;
    isOpen: boolean;
    toggleOpen: (isOpen?: boolean) => void;
  }
>((props, ref) => {
  const {children, isOpen, toggleOpen, ...otherProps} = props;
  const classes = clsx(
    'absolute mt-8 w-full list-none rounder p-0 z-[10]',
    'border-[1px] border-solid border-[light-dark(rgba(34,36,38,.15),_rgba(255,255,255,.15))] overflow-hidden',
    'bg-[light-dark(#fff,_var(--moon-850))] max-h-[250px]',
    'shadow-[0_2px_4px_0_light-dark(rgba(34,36,38,.12),_rgba(0,0,0,.4)),0_2px_10px_0_light-dark(rgba(34,36,38,.15),_rgba(0,0,0,.5))]',
    {hidden: !isOpen}
  );

  return (
    <ul
      className={classes}
      ref={ref}
      {...otherProps}
      data-test="filter-simple-value-dropdown-wrapper">
      {props.children}
    </ul>
  );
});

const ListItem = React.forwardRef<
  HTMLLIElement,
  {
    isHighlighted: boolean;
    isSelected: boolean;
    value: string;
  }
>((props, ref) => {
  const {isHighlighted, ...otherProps} = props;
  const classes = clsx(
    'hover:bg-[light-dark(rgba(0,0,0,.05),_rgba(255,255,255,.08))] hover:text-[light-dark(rgba(0,0,0,.95),_var(--moon-100))]',
    'truncate leading-18 m-0 cursor-pointer item',
    {
      'bg-[light-dark(rgba(0,0,0,.05),_rgba(255,255,255,.08))] text-[light-dark(rgba(0,0,0,.95),_var(--moon-100))]':
        isHighlighted,
    }
  );
  return <li ref={ref} className={classes} {...otherProps} />;
});

const ItemLabel = ({
  isSelected,
  label,
}: {
  isSelected: boolean;
  label: string;
}) => {
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  // Only show tooltip when text is actually truncated
  const handleOpenChange = React.useCallback((open: boolean) => {
    if (open) {
      const el = textRef.current;
      if (el && el.scrollWidth > el.clientWidth) {
        setIsOpen(true);
      }
    } else {
      setIsOpen(false);
    }
  }, []);

  return (
    <RadixTooltip.Provider delayDuration={300}>
      <RadixTooltip.Root open={isOpen} onOpenChange={handleOpenChange}>
        <RadixTooltip.Trigger asChild>
          <div className="flex w-full justify-between px-12 py-[13px]">
            <span ref={textRef} className="truncate">
              {label}
            </span>
            {isSelected && <Icon name="checkmark" />}
          </div>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side="top"
            // Matches zIndex from @wandb/weave Tooltip — must exceed Semantic UI modal (2147483605)
            style={{zIndex: 2147483606}}
            className="max-w-[400px] whitespace-normal break-words">
            {label}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};

const InputElement = React.forwardRef((props: any, ref) => {
  const {className, ...otherProps} = props;
  delete otherProps.toggleSelection;

  return (
    <input
      className={`m-0 w-full cursor-text overflow-visible border-0 bg-[light-dark(#fff,_var(--moon-850))] px-12 py-5 text-[light-dark(var(--moon-800),_var(--moon-200))] text-base leading-none shadow-none focus-visible:border-0`}
      ref={ref}
      placeholder={`Search values`}
      {...otherProps}
      // this has to come after the spread to avoid accidental overwriting
      onClick={e => {
        e.stopPropagation();
      }}
    />
  );
});

export const FilterSimpleValueDropdownComponents = {
  Container,
  Display,
  Input: InputElement,
  ListItem,
  ItemLabel,
  ListWrapper,
};

export const FilterSimpleValueInfiniteDropdown = makeInfiniteDropdown(
  FilterSimpleValueDropdownComponents,
  {
    extractItems: (data: any): string[] => {
      return (
        data?.project?.runFieldValues?.edges
          ?.filter((edge: {node: {value: Maybe<string>}}) => {
            // Allow null values through - they'll display as 'null'
            if (edge.node.value == null) {
              return true;
            }
            try {
              const parsedValue = JSON.parse(edge.node.value);
              const valueType = parsedValue?._type;
              // Since media types aren't simple values, we just want to enable filtering by null/non-null for now. So filter out
              // media values.
              return !isMediaString(valueType as MediaString);
            } catch (e) {
              // in case of JSON parse error, we don't want to show the value
              return false;
            }
          })
          ?.map((edge: {node: {value: Maybe<string>}}) => {
            return Run.displayValue(edge.node.value, 'null');
          }) ?? []
      );
    },
    extractPageInfo: (data: any) => {
      return data?.project?.runFieldValues?.pageInfo ?? {};
    },
    query: RunValueSuggestionsPaginatedDocument,
  }
);
